import React from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from 'utils/time';
import { Group } from '../../../../store/api/groups';
import { TabPanel } from '../components/TabPanel';
import { SettingRow } from '../components/SettingRow';
import styles from '../TargetGroupSettings.module.css';

interface GeneralTabProps {
    tab: string;
    index: number;
    selectedIndex: number;
    companySlug: string;
    assessmentId: string;
    scanName: string;
    language: string;
    isSuperAdmin: boolean;
    group?: Group;
}

/**
 * Read-only tab. Surfaces scan name, language, opt-out questionnaire status,
 * and expiration date. No save button — none of these fields are editable
 * from this modal.
 */
export const GeneralTab = ({
    tab,
    index,
    selectedIndex,
    companySlug,
    assessmentId,
    scanName,
    language,
    isSuperAdmin,
    group,
}: GeneralTabProps) => {
    const showOptOutRow =
        (isSuperAdmin || companySlug === 'bunq') &&
        group?.type !== 'MOTIVIEW';

    return (
        <TabPanel tab={tab} index={index} selectedIndex={selectedIndex}>
            <table>
                <colgroup>
                    <col className={styles.setting_key} />
                    <col className={styles.setting_value} />
                </colgroup>
                <tbody>
                    <SettingRow
                        label="Scan name"
                        description="Which scan this group belongs to">
                        <Link to={`/${companySlug}/scans/${assessmentId}`}>
                            <span>{scanName ? scanName : 'unavailable'}</span>
                        </Link>
                    </SettingRow>

                    <SettingRow
                        label="Language"
                        description="Language of the scan, reports and emails for this group">
                        <span>{language ? language.toUpperCase() : ''}</span>
                    </SettingRow>

                    {showOptOutRow && (
                        <SettingRow
                            label="Opt-out questionnaire"
                            description={
                                <p>
                                    Displays a brief scientific questionnaire for
                                    candidates who choose the opt-out flow,
                                    enabling Engagement and Stress resilience
                                    scores to be generated.
                                    <br />
                                    <i>
                                        If disabled, opt-out candidates will not
                                        receive these scores.
                                    </i>
                                </p>
                            }>
                            <span>
                                {group?.config?.has_opt_out_questionnaire
                                    ? 'Active'
                                    : 'Not active'}
                            </span>
                        </SettingRow>
                    )}

                    <SettingRow
                        label="Expiration date"
                        description="This group is active until the expiration date.">
                        <span>
                            {group && group.expiration_date
                                ? `Ends on ${formatDate(group.expiration_date)}`
                                : 'No Expiration Date'}
                        </span>
                    </SettingRow>
                </tbody>
            </table>
        </TabPanel>
    );
};

export default GeneralTab;
